// src/components/pipelines/PipelineClearancePanel.js
// ============================================================================
// Register-wide view of how much of the pipeline network the 4D subsurface
// clearance scan can actually use, plus an inline depth backfill worklist.
//
// WHY A DEDICATED PANEL
// ---------------------
// Two numbers decide whether excavation clearance is trustworthy, and neither
// is visible anywhere else in the app:
//
//   1. SCAN VISIBILITY — a pipeline whose geometry is not SRID 4326 raises
//      MySQL error 3033 inside the scan. The service catches it and returns
//      INDETERMINATE, which reaches the crew as a blocked work order with no
//      explanation. A register can be 100% populated and 0% usable.
//
//   2. DEPTH COVERAGE — NULL depth is treated as in-scope on purpose, because
//      unknown is not safe. The cost is that an unsurveyed main is flagged on
//      every excavation at every depth. At low coverage every scan returns a
//      wall of conflicts and crews learn to click through them.
//
// The worklist is ranked by diameter: a 24" trunk with no recorded depth is a
// far larger clearance problem than a 2" service line.
// ============================================================================

import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box, VStack, HStack, Text, Heading, Progress, Badge, Button, IconButton,
  Table, Thead, Tbody, Tr, Th, Td, Input, Select, Tooltip, Icon, Alert,
  AlertIcon, AlertTitle, AlertDescription, SimpleGrid, Divider, Spinner,
  useColorModeValue, useToast, Flex, InputGroup, InputLeftElement,
} from '@chakra-ui/react';
import {
  MdRefresh, MdWarning, MdCheckCircle, MdErrorOutline, MdSearch,
  MdEdit, MdSave, MdRouter,
} from 'react-icons/md';
import { FaArrowDown, FaShieldAlt } from 'react-icons/fa';

import * as PipelineService from '../../services/pipelineService';
import { DEPTH_SOURCES, PIPELINE_STATUSES } from '../../services/pipelineService';

const StatTile = ({ label, value, sub, color, tileBg, tileBorder, mutedColor }) => (
  <Box bg={tileBg} borderWidth="1px" borderColor={tileBorder} borderRadius="xl" p={3}>
    <Text fontSize="10px" fontWeight="700" color={mutedColor} textTransform="uppercase" letterSpacing="0.06em">{label}</Text>
    <Text fontSize="2xl" fontWeight="800" color={color} lineHeight="1.15">{value}</Text>
    {sub && <Text fontSize="10px" color={mutedColor}>{sub}</Text>}
  </Box>
);

function PipelineClearancePanel({ pipelines = [], isLoading = false, onRefresh, onEditPipeline }) {
  // Hoisted color-mode values.
  const cardBg = useColorModeValue('white', 'gray.800');
  const tileBg = useColorModeValue('gray.50', 'gray.900');
  const tileBorder = useColorModeValue('gray.100', 'gray.700');
  const border = useColorModeValue('gray.200', 'gray.700');
  const headerBg = useColorModeValue('gray.50', 'gray.900');
  const inputBg = useColorModeValue('white', 'gray.700');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const okColor = useColorModeValue('green.600', 'green.300');
  const warnColor = useColorModeValue('orange.600', 'orange.300');
  const badColor = useColorModeValue('red.600', 'red.300');

  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [draftDepth, setDraftDepth] = useState('');
  const [draftSource, setDraftSource] = useState('field_survey');
  const [savingId, setSavingId] = useState(null);
  const toast = useToast();

  // --- Coverage, computed from the already-fetched register ----------------
  // No extra API call: fetchPipelines attaches everything needed. The
  // is_clearance_visible flag comes from the service and accounts for both
  // SRID and lifecycle status.
  const coverage = useMemo(() => {
    const total = pipelines.length;
    if (total === 0) {
      return { total: 0, scanVisible: 0, scanVisiblePct: 100, withDepth: 0, depthPct: 0, sridBroken: 0, noGeometry: 0, excluded: 0, surveyGrade: 0, estimated: 0 };
    }

    let scanVisible = 0, withDepth = 0, sridBroken = 0, noGeometry = 0, excluded = 0, surveyGrade = 0, estimated = 0;
    const excludedStatuses = new Set(PIPELINE_STATUSES.filter(s => !s.scanned).map(s => s.value));

    pipelines.forEach(p => {
      if (p.is_clearance_visible) scanVisible += 1;
      if (p.depth_m !== null && p.depth_m !== undefined) withDepth += 1;
      if (p.geometry_srid_ok === false) sridBroken += 1;
      if (!Array.isArray(p.path_coordinates) || p.path_coordinates.length < 2) noGeometry += 1;
      if (excludedStatuses.has(p.status)) excluded += 1;
      if (p.depth_source === 'field_survey') surveyGrade += 1;
      if (p.depth_source === 'estimated') estimated += 1;
    });

    return {
      total,
      scanVisible,
      scanVisiblePct: Number(((100 * scanVisible) / total).toFixed(1)),
      withDepth,
      depthPct: Number(((100 * withDepth) / total).toFixed(1)),
      sridBroken, noGeometry, excluded, surveyGrade, estimated,
    };
  }, [pipelines]);

  // Backfill worklist: scan-visible mains with no depth, largest first.
  const depthGaps = useMemo(() => {
    const q = search.trim().toLowerCase();
    return pipelines
      .filter(p => (p.depth_m === null || p.depth_m === undefined) && p.is_clearance_visible !== false)
      .filter(p => !q
        || String(p.name || '').toLowerCase().includes(q)
        || String(p.area_code || '').toLowerCase().includes(q)
        || String(p.material || '').toLowerCase().includes(q))
      .sort((a, b) => (Number(b.diameter_inches) || 0) - (Number(a.diameter_inches) || 0))
      .slice(0, 100);
  }, [pipelines, search]);

  const beginEdit = useCallback((p) => {
    setEditingId(p.id);
    setDraftDepth('');
    setDraftSource('field_survey');
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setDraftDepth('');
  }, []);

  const saveDepth = useCallback(async (p) => {
    setSavingId(p.id);
    try {
      // setPipelineDepth is the narrow write — it touches depth only, so a
      // backfill pass can never disturb geometry or status.
      await PipelineService.setPipelineDepth(p.id, draftDepth, draftSource);
      toast({
        title: 'Depth recorded',
        description: `${p.name || `Pipeline ${p.id}`} → ${draftDepth} m`,
        status: 'success', duration: 2500, isClosable: true, position: 'bottom-right',
      });
      setEditingId(null);
      setDraftDepth('');
      if (onRefresh) onRefresh();
    } catch (err) {
      const detail = err.details?.length ? err.details.join(' ') : err.message;
      toast({ title: 'Could not save depth', description: detail, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setSavingId(null);
    }
  }, [draftDepth, draftSource, onRefresh, toast]);

  if (isLoading && pipelines.length === 0) {
    return (
      <Flex justify="center" align="center" py={12}>
        <VStack spacing={3}><Spinner color="purple.500" /><Text fontSize="sm" color={mutedColor}>Loading pipeline register…</Text></VStack>
      </Flex>
    );
  }

  return (
    <Box>
      {/* --- SRID alarm: the failure that makes the scan unusable ---------- */}
      {coverage.sridBroken > 0 && (
        <Alert status="error" borderRadius="xl" mb={4} flexDirection="column" alignItems="flex-start">
          <HStack><AlertIcon as={MdErrorOutline} /><AlertTitle fontSize="sm">Clearance scans are failing for {coverage.sridBroken} pipeline(s)</AlertTitle></HStack>
          <AlertDescription fontSize="xs" mt={1} pl={7}>
            Their geometry is not SRID 4326, so the subsurface scan raises a database error against them
            and returns INDETERMINATE — which reaches the crew as a blocked work order with no usable
            explanation. Run the SRID repair migration; recording depth will not fix it.
          </AlertDescription>
        </Alert>
      )}

      {coverage.total > 0 && coverage.depthPct < 50 && coverage.sridBroken === 0 && (
        <Alert status="warning" borderRadius="xl" mb={4} flexDirection="column" alignItems="flex-start">
          <HStack><AlertIcon /><AlertTitle fontSize="sm">Only {coverage.depthPct}% of mains have a recorded depth</AlertTitle></HStack>
          <AlertDescription fontSize="xs" mt={1} pl={7}>
            Unknown depth is treated as in-scope, so those mains are flagged on every excavation at every
            depth. That is the safe default, but at this coverage level a clearance scan returns a wall of
            conflicts and stops carrying information.
          </AlertDescription>
        </Alert>
      )}

      {/* --- Coverage tiles ------------------------------------------------ */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={5}>
        <StatTile
          label="Registered" value={coverage.total}
          sub={`${coverage.excluded} excluded by status`}
          color={undefined} tileBg={tileBg} tileBorder={tileBorder} mutedColor={mutedColor}
        />
        <StatTile
          label="Scan visible" value={`${coverage.scanVisiblePct}%`}
          sub={`${coverage.scanVisible} of ${coverage.total}`}
          color={coverage.scanVisiblePct >= 95 ? okColor : coverage.scanVisiblePct >= 70 ? warnColor : badColor}
          tileBg={tileBg} tileBorder={tileBorder} mutedColor={mutedColor}
        />
        <StatTile
          label="Depth recorded" value={`${coverage.depthPct}%`}
          sub={`${coverage.withDepth} of ${coverage.total}`}
          color={coverage.depthPct >= 80 ? okColor : coverage.depthPct >= 40 ? warnColor : badColor}
          tileBg={tileBg} tileBorder={tileBorder} mutedColor={mutedColor}
        />
        <StatTile
          label="Survey grade" value={coverage.surveyGrade}
          sub={`${coverage.estimated} estimated only`}
          color={undefined} tileBg={tileBg} tileBorder={tileBorder} mutedColor={mutedColor}
        />
      </SimpleGrid>

      <Box mb={5}>
        <HStack justify="space-between" mb={2}>
          <HStack spacing={2}>
            <Icon as={FaShieldAlt} color="orange.400" boxSize={3} />
            <Text fontSize="xs" fontWeight="700">Depth coverage</Text>
          </HStack>
          <Text fontSize="xs" fontWeight="700">{coverage.withDepth} / {coverage.total}</Text>
        </HStack>
        <Progress
          value={coverage.depthPct} size="sm" borderRadius="full"
          colorScheme={coverage.depthPct >= 80 ? 'green' : coverage.depthPct >= 40 ? 'orange' : 'red'}
        />
      </Box>

      <Divider mb={4} />

      {/* --- Backfill worklist --------------------------------------------- */}
      <Flex justify="space-between" align="center" mb={3} flexWrap="wrap" gap={3}>
        <Box>
          <Heading size="sm" fontWeight="700">Depth backfill worklist</Heading>
          <Text fontSize="xs" color={mutedColor}>
            Mains with no recorded cover depth, largest diameter first.
          </Text>
        </Box>
        <HStack spacing={2}>
          <InputGroup size="sm" maxW="200px">
            <InputLeftElement><Icon as={MdSearch} color="gray.400" /></InputLeftElement>
            <Input placeholder="Filter…" value={search} onChange={e => setSearch(e.target.value)} bg={inputBg} borderRadius="lg" />
          </InputGroup>
          <Tooltip label="Reload register" hasArrow>
            <IconButton icon={<Icon as={MdRefresh} />} size="sm" variant="ghost" onClick={onRefresh} isLoading={isLoading} aria-label="Refresh" borderRadius="full" />
          </Tooltip>
        </HStack>
      </Flex>

      {depthGaps.length === 0 ? (
        <Box py={10} textAlign="center" borderWidth="1px" borderStyle="dashed" borderColor={border} borderRadius="xl">
          <Icon as={MdCheckCircle} boxSize={10} color={okColor} mb={2} />
          <Text fontWeight="700" fontSize="sm">
            {search ? 'No mains match that filter' : 'Every scan-visible main has a recorded depth'}
          </Text>
          {!search && (
            <Text fontSize="xs" color={mutedColor} mt={1}>
              Clearance results will flag only the mains an excavation can actually reach.
            </Text>
          )}
        </Box>
      ) : (
        <Box overflowX="auto" borderWidth="1px" borderColor={border} borderRadius="xl">
          <Table size="sm" variant="simple">
            <Thead bg={headerBg}>
              <Tr>
                <Th fontSize="9px">Pipeline</Th>
                <Th fontSize="9px" isNumeric>Ø (in)</Th>
                <Th fontSize="9px">Material</Th>
                <Th fontSize="9px">Zone</Th>
                <Th fontSize="9px" w="290px">Record cover depth</Th>
              </Tr>
            </Thead>
            <Tbody>
              {depthGaps.map(p => (
                <Tr key={p.id}>
                  <Td>
                    <HStack spacing={2}>
                      <Icon as={MdRouter} color="purple.400" boxSize={3} />
                      <VStack align="start" spacing={0}>
                        <Text fontSize="xs" fontWeight="700">{p.name || `Pipeline ${p.id}`}</Text>
                        <Text fontSize="10px" color={mutedColor}>ID {p.id}</Text>
                      </VStack>
                    </HStack>
                  </Td>
                  <Td isNumeric>
                    <Badge
                      colorScheme={Number(p.diameter_inches) >= 12 ? 'red' : Number(p.diameter_inches) >= 6 ? 'orange' : 'gray'}
                      borderRadius="full" fontSize="10px"
                    >
                      {p.diameter_inches ?? '—'}
                    </Badge>
                  </Td>
                  <Td><Text fontSize="xs" color={mutedColor}>{p.material || '—'}</Text></Td>
                  <Td><Text fontSize="xs" color={mutedColor}>{p.area_code || '—'}</Text></Td>
                  <Td>
                    {editingId === p.id ? (
                      <HStack spacing={2}>
                        <InputGroup size="xs" maxW="90px">
                          <InputLeftElement><Icon as={FaArrowDown} color="gray.400" boxSize={2.5} /></InputLeftElement>
                          <Input
                            type="number" step="0.05" min="0" max="30" autoFocus
                            placeholder="m" value={draftDepth}
                            onChange={e => setDraftDepth(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter' && draftDepth !== '') saveDepth(p);
                              if (e.key === 'Escape') cancelEdit();
                            }}
                            bg={inputBg} borderRadius="md"
                          />
                        </InputGroup>
                        <Select size="xs" maxW="115px" value={draftSource} onChange={e => setDraftSource(e.target.value)} bg={inputBg} borderRadius="md">
                          {DEPTH_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </Select>
                        <IconButton
                          icon={<Icon as={MdSave} />} size="xs" colorScheme="green"
                          onClick={() => saveDepth(p)} isLoading={savingId === p.id}
                          isDisabled={draftDepth === ''} aria-label="Save depth" borderRadius="md"
                        />
                        <Button size="xs" variant="ghost" onClick={cancelEdit}>Cancel</Button>
                      </HStack>
                    ) : (
                      <HStack spacing={2}>
                        <Button size="xs" colorScheme="purple" variant="outline" borderRadius="md"
                          leftIcon={<Icon as={FaArrowDown} boxSize={2.5} />} onClick={() => beginEdit(p)}>
                          Record depth
                        </Button>
                        {onEditPipeline && (
                          <Tooltip label="Edit all clearance fields" hasArrow>
                            <IconButton icon={<Icon as={MdEdit} />} size="xs" variant="ghost"
                              onClick={() => onEditPipeline(p)} aria-label="Edit pipeline" borderRadius="md" />
                          </Tooltip>
                        )}
                      </HStack>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {depthGaps.length >= 100 && (
        <Text fontSize="10px" color={mutedColor} mt={2} textAlign="right">
          Showing the 100 largest. Record these first and reload for the next batch.
        </Text>
      )}
    </Box>
  );
}

PipelineClearancePanel.propTypes = {
  pipelines: PropTypes.array,
  isLoading: PropTypes.bool,
  onRefresh: PropTypes.func,
  onEditPipeline: PropTypes.func,
};

export default PipelineClearancePanel;